import type {Component} from 'grapesjs';
import type {ColorPaletteInfo, ColorPalettePill} from '@Components/auto-branding/auto-branding.types';
import type {SelectedValueType} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import type WalkthroughPopup from '@/email-maker/grapesjs-email-editor-plugin/src/vos/walkthroughpopup';

export default interface EmailMakerConstants {
  devices: DeviceSettings;
  walkthrough: WalkthroughPopupDefinitions;
  exportOptions: EmailExportOptionsConstants;
  blocks: BlocksConstants;
  commands: CommandsConstants;
  components: ComponentsConstants;
  buttonPresets: Record<number, number>;
  props: Record<string, string>;
  styles: Record<string, string>;
  mediaType: MediaTypes;
  mediaImageModal: MediaImageModalTypes;
  isInitializingEditor: boolean;
  RTE_INIT: boolean;
  AUTOSAVE_STATE_SAVING: string;
  AUTOSAVE_STATE_NOT_SAVED: string;
  AUTOSAVE_STATE_SAVED: string;
  DEVICE_CLASS_PREFIX: string;
  DEVICE_DESKTOP: string;
  DEVICE_MOBILE: string;
  DEVICE_TABLET: string;
  AUTO_WIDTH_CTA_CLASS: string;
  EMAIL_DATA_CLIPBOARD_KEY: string;
  EMAIL_COMPONENT_DROPPABLE_KEY: string;
  HIGHLIGHT_HOVER_CLASS: string;
  LOCAL_STORAGE_POSTER_ID_KEY: string;
  ADD_DESIGN_TO_EMAIL_TEMPLATE: string;
  READ_ONLY_FOOTER_ID: string;
  EMAIL_OVERLAY_ID: string;
  EMAIL_MEDIA_OVERLAY_ID: string;
  EMAIL_OVERLAY_ANIMATION_TIME_MS: number;
  COMPONENT_LOADING_OVERLAY_CLASS: string;
  MORE_OPTIONS_DIALOG: string;
  EXPORT_OPTIONS_DIALOG: string;
  EXPORT_OPTIONS_DIALOG_MOBILE: string;
  MAX_HTML_CONTENT_LENGTH: number;
  MAX_VIDEOS_ALLOWED: number;
  MAX_TRIES_TO_FIND_TOOLBAR: number;
  MOBILE_CSS_PROP_PREFIX: string;
  MODE_START: string;
  MODE_LOAD: string;
  MODE_COPY: string;
  NEW_TEMPLATE_NAME: string;
  TABLET_CSS_PROP_PREFIX: string;
  ZOOM_STEP: number;
  ZOOM_VALUE: number;
  DEFAULT_ZOOM: number;
  ZOOM_UPPER_LIMIT: number;
  ZOOM_LOWER_LIMIT: number;
  IS_SCHEDULED: boolean;
  MINUTES_STEP: number;
  RTE_TAGNAMES: string[];
  PUBLISH_CTA: JQuery<HTMLElement>;
  SCHEDULED_TIME: string;
  IS_SAVING: boolean;
  HAS_SAVED: boolean;
  SAVE_QUEUED: boolean;
  SAVE_BUTTON_TITLE: string;
  COPIED_STYLES_COMPONENT: Component | null;
  NONCOPYABLE_STYLES: string[];
  CUSTOM_EVENT_STORAGE_END: string;
  CUSTOM_EVENT_COMPONENT_STYLE_COPIED: string;
  NUM_REMOVE_BACKGROUND_CREDITS: number;
  SIGNUP_FORMS_DATA: UserSignupFormsData;
  COLOR_PALETTES: ColorPaletteInfo[] | undefined;
  COLOR_PALETTE_PILLS: ColorPalettePill[] | undefined;
  DESIGN_COPY: string;
  DESIGN_EDIT: string;
  BUTTON_DEFAULT_PRESET_ID: number;
  WIDTH_DEFAULT_TOGGLE: number;
  WALKTHROUGH_CONTAINER_CLASSNAME: string;
  TOGGLE_OPTION_PRESET_ID_ONE: number;
  TOGGLE_OPTION_PRESET_ID_ZERO: number;
  IS_WALKTHROUGH_POPUP_OPEN: boolean;
  DIVIDER_DEFAULT_HEIGHT: string;
  AUTO_UPDATE_EMAIL_CONTENT_PARAMS?: AutoUpdateEmailContentParams;
  PUPPETEER_CONFIG: EmailMakerPuppeteerConfig | null;
}

export interface MediaTypes {
  IMAGE: 'image';
  VIDEO: 'video';
  ANIMATED_STICKER: 'animated_sticker';
  STILL_STICKER: 'still_sticker';
}

export interface MediaImageModalTypes {
  MY_UPLOADS: 'my-uploads-images';
  EXPLORE_MEDIA: 'explore-media-images';
  AI_IMAGES: 'ai-images';
}

export interface WalkthroughPopupDefinitions {
  MAIN_WALKTHROUGH: WalkthroughPopup;
  INTRODUCTION_MAIN_WALKTHROUGH: WalkthroughPopup;
  HIERARCHY_LEVEL_ONE: WalkthroughPopup;
  HIERARCHY_LEVEL_TWO: WalkthroughPopup;
  ADD_SECTION: WalkthroughPopup;
  ADD_TABLE: WalkthroughPopup;
  ADD_BUTTON: WalkthroughPopup;
  ADD_LINK: WalkthroughPopup;
  ADD_SOCIAL_LINKS: WalkthroughPopup;
  ADD_DESIGN: WalkthroughPopup;
}

export interface EmailExportOptionsConstants {
  COPY_EMAIL: number;
  COPY_HTML: number;
  DOWNLOAD_HTML: number;
}

export interface BlocksConstants {
  CATEGORY_SECTIONS: string;
  CATEGORY_ELEMENTS: string;
  CATEGORY_TEXT: string;
  CATEGORY_IMAGE: string;
  CATEGORY_MY_DESIGNS: string;
  ID_BLOCKS_MANAGER_CONTAINER: string;
  ID_SECTION_PRESET_1: string;
  ID_SECTION_PRESET_2: string;
  ID_SECTION_PRESET_3: string;
  ID_SECTION_PRESET_4: string;
  ID_SECTION_PRESET_5: string;
  ID_SECTION_PRESET_6: string;
  ID_TEXT_PRESET_1: string;
  ID_TEXT_PRESET_2: string;
  ID_UPLOAD_CONTENT: string;
  ID_EXPLORE_MEDIA: string;
  ID_AI_IMAGES: string;
  ID_DESIGN: string;
  ID_BUTTON: string;
  ID_SOCIAL: string;
  ID_DIVIDER: string;
  ID_TABLE: string;
  ID_STICKER: string;
  ID_VIDEO: string;
  ID_ADD_BLOCKS_DIALOG: string;
}

type CommandsConstants = Record<string, string> & CommandsTypes;

export interface CommandsTypes {
  walkthrough: Record<string, string>;
  text: Record<string, string>;
  mediaModals: Record<string, string>;
}

export type StyleAttributes = Record<string, string>;

export interface BasicComponentCommandProps {
  component?: Component;
}

export interface ComponentsConstants {
  default: Record<string, string>;
  table: Record<string, string>;
  social: Record<string, string>;
  graphic: Record<string, string>;
  container: Record<string, string>;
  text: Record<string, string>;
  misc: Record<string, string>;
}

export interface DeviceSettings {
  DEVICE_CLASS_PREFIX: string;
  DEVICE_DESKTOP: string;
  DEVICE_MOBILE: string;
  DEVICE_TABLET: string;
  AUTO_WIDTH_CTA_CLASS: string;
}

export interface SymbolStorageData {
  content?: string;
  type?: string;
}

export interface EmailMaker {
  devices: DeviceSettings;
  walkthrough: WalkthroughPopupDefinitions;
  exportOptions: EmailExportOptionsConstants;
  blocks: BlocksConstants;
  commands: Record<string, string>;
}

export enum ResponsiveType {
  MOBILE = 'MOBILE',
  TABLET = 'TABLET',
}

export enum ResponsiveAttributeName {
  MOBILE = 'responsive',
  TABLET = 'responsive-tablet',
}

export enum OnLoadUrlParamsConfig {
  AutoUpdateEmailContentParams = 'autoUpdateEmailContentParams',
}

export interface EmailPreviewResponse {
  fileName: string;
  html: string;
}

export interface UserSignupFormsData {
  limit: number;
  offset: number;
  signupForms: UserSignupFormLinkData[];
}

export const mailingListUpdateEvent = 'mailingList::update';

export interface UserSignupFormLinkData {
  name: string;
  url: string;
}

export interface AutoUpdateTextContentParams {
  rewriteContentPrompt: string;
  rewriteContentPromptOptions: SelectedValueType;
}

export type HexColors = string[];

export interface AutoUpdateBrandingParams {
  colors: HexColors;
}

export interface AutoUpdateEmailContentParams {
  autoUpdateEmailTemplateOnLoad: boolean;
  autoUpdateTextContentParams?: AutoUpdateTextContentParams;
  autoUpdateBrandingParams?: AutoUpdateBrandingParams;
}

export interface GA4TrackingEvents {
  SECTIONS_USED: number;
}

export const PROMPT_SESSION_STORAGE_VAR = 'prompt-session-storage-var';
export const GENERATION_OPTIONS_SESSION_STORAGE_VAR = 'generation-options-session-storage-var';
export const TEMPLATE_AI_REGENERATION = 'template-ai-regeneration';
export const TOOLBAR_AI_REWRITE_DROPDOWN_ANCHOR = 'toolbar-ai-rewrite-dropdown-anchor';
export const DISABLE_GJS_TOOLBAR_ACTIVE_STYLE = 'disable-gjs-toolbar-active-style';
export const TEXT_COMPONENT_AI_REWRITE_PROMPT_BOX_INDEX = 5;
export const BUTTON_COMPONENT_AI_REWRITE_PROMPT_BOX_INDEX = 5;

export enum ComponentloadingOverlayStylesTarget {
  COMPONENT_DEFINITION = 'COMPONENT_DEFINITION',
  ELEMENT = 'ELEMENT',
}

export interface ComponentloadingOverlayStyle {
  height: string;
  width: string;
  transform: string;
  marginLeft: string;
  borderRadius: string;
}

export interface EmailMakerPuppeteerConfig {
  flow: EmailMakerPuppeteerFlow;
}

export enum EmailMakerPuppeteerFlow {
  TEMPLATE_AUTO_BRANDING = 'TEMPLATE_AUTO_BRANDING',
}

export interface CanUseBypassedEmailResponse {
  canUseEmail: boolean;
}


